import { createFeatureSelector, createSelector } from '@ngrx/store';

import { CUSTOM_REDUCER_KEY } from '../reducers/custom.reducer';
import { CustomState } from '../states/custom.state';
import {
    getAppsMetaDataMap$,
    getAppsMetaDataPermissionsMap$,
    getAssetsWithLatestVersion$,
    getSubscribedAppsApiMap$,
} from './apps.selector';
import {
    getAllMapTransformations$,
    getCollaborators$,
    getDownloadableFiles$,
    getInstanceIntegrationStatus$,
    getInstancePreCapturedAttributes$,
    getInstancesOrgServicesMap$,
    getServicesInstancesMap$,
} from './instances.selector';
import { getCurrentOrganization$ } from './locations.selector';
import { getEventTypes$ } from './notifications.selector';
import { getSessionUserPermissions$ } from './permissions.selector';
import { getAuditChangedEntities$, getRecordLogs$, getRecords$, getRecordsOrg$ } from './records.selector';
import { getReportsMap$ } from './reports.selector';
import { getOutboundTransformationsMap$ } from './transformations.selector';
import { getWorkflowInstances$ } from './workflow.selector';

const currentState = createFeatureSelector<CustomState>(CUSTOM_REDUCER_KEY);

export const getCustomOrganizationId$ = createSelector(currentState, state => state.currentOrganizationId);

export const getCustomCurrentOrganization$ = createSelector(getCurrentOrganization$, organization => organization);

export const getCustomInstanceId$ = createSelector(currentState, state => state.instanceId);

export const getCustomInstance$ = createSelector(
    getServicesInstancesMap$,
    getCustomInstanceId$,
    (instancesMap, instanceId) => instancesMap?.[instanceId]
);

export const getCustomAppServiceId$ = createSelector(currentState, state => state.appServiceId);

export const getCustomAppsRestApiName$ = createSelector(currentState, state => state.appRestApiName);

export const getCustomBridgeRestApiName$ = createSelector(currentState, state => state.bridgeRestApiName);

export const getCustomSelectedEntityParentRecordId$ = createSelector(currentState, state => state.selectedEntityParentRecordId);

export const getCustomSelectedEntityUId$ = createSelector(currentState, state => state.selectedEntityUId);

export const getCustomAppDetails$ = createSelector(
    getCustomAppsRestApiName$,
    getSubscribedAppsApiMap$,
    getAssetsWithLatestVersion$,
    (appRestApiName, appsMetaDataMap, versionsMap) => {
        const app = appsMetaDataMap?.[appRestApiName];
        return app !== undefined
            ? {
                  ...app,
                  assetMetaUId: versionsMap?.[app?.serviceId]?.metadataId,
              }
            : undefined;
    }
);

export const getCustomAppsMetaData$ = createSelector(
    getCustomAppDetails$,
    getAppsMetaDataMap$,
    (appDetails, appsMetaDataMap) => appsMetaDataMap?.[appDetails?.serviceId]?.[appDetails?.assetMetaUId]
);

export const getCustomAppMetaDataPermissions$ = createSelector(
    getCustomAppServiceId$,
    getAppsMetaDataPermissionsMap$,
    (serviceId, appsMetaDataPermissionsMap) => appsMetaDataPermissionsMap?.[serviceId]
);

export const getCustomSessionUserPermissions$ = createSelector(getSessionUserPermissions$, permissions => permissions);

export const getInstancesIds$ = createSelector(
    getInstancesOrgServicesMap$,
    getCustomAppDetails$,
    (instancesMap, app) => instancesMap?.[app?.serviceId]?.processInstances
);

export const getCustomInstancesPagination$ = createSelector(
    getInstancesOrgServicesMap$,
    getCustomAppDetails$,
    getInstancesIds$,
    (instancesMap, app, instanceIds) => {
        const page = instancesMap?.[app?.serviceId]?.pagination || { size: 0, pageIndex: 0, pagingState: undefined };
        const fetchedCount = instanceIds?.length || 0;
        const pageCount = (page?.pageIndex + 1) * page?.size;
        const count = (page?.pagingState ? pageCount : fetchedCount) + (page?.pagingState !== undefined ? 1 : 0);
        return {
            ...page,
            count,
        };
    }
);

const getCustomPaginatedInstanceIds$ = createSelector(getCustomInstancesPagination$, getInstancesIds$, (pagination, instanceIds) => {
    const start = (pagination?.pageIndex || 0) * (pagination?.size || 0);
    const end = ((pagination?.pageIndex || 0) + 1) * (pagination?.size || 0);
    return instanceIds?.filter((_instanceId, index) => index >= start && index < end);
});

export const getCustomInstances$ = createSelector(getServicesInstancesMap$, getCustomPaginatedInstanceIds$, (instancesMap, instanceIds) =>
    instanceIds?.reduce((instances, id) => {
        instances.push(instancesMap?.[id]);
        return instances;
    }, [])
);

export const getPreCapturedAttributes$ = createSelector(
    getInstancePreCapturedAttributes$,
    preCapturedAttributes => preCapturedAttributes || {}
);

export const getIntegrationStatus$ = createSelector(getInstanceIntegrationStatus$, integrationStatus => integrationStatus || {});

export const getInstanceReportsMap$ = createSelector(getReportsMap$, reports => reports || {});

export const getInstanceEventTypes$ = createSelector(getEventTypes$, eventTypes => eventTypes);

export const getInstanceTransformations$ = createSelector(getAllMapTransformations$, transformations => transformations || {});

export const getInstanceDownloadableFiles$ = createSelector(getDownloadableFiles$, downloadableFiles => downloadableFiles || {});

export const getInstanceCollaborators$ = createSelector(getCollaborators$, collaborators => collaborators || {});

export const getServiceOutBoundTransformations$ = (serviceId: string) =>
    createSelector(getOutboundTransformationsMap$(serviceId), transformations => transformations || []);

export const getCustomAuditChangedEntities$ = createSelector(getAuditChangedEntities$, auditChangedEntities => auditChangedEntities);

const getCustomRecordsOrgMap$ = createSelector(
    getCustomAppServiceId$,
    getCustomInstanceId$,
    getRecordsOrg$,
    (serviceId, instanceId, recordOrgsMap) => {
        return recordOrgsMap?.[serviceId]?.[instanceId];
    }
);

export const getCustomRecordsEntityMap$ = createSelector(
    getCustomRecordsOrgMap$,
    getCustomSelectedEntityParentRecordId$,
    getCustomSelectedEntityUId$,
    (recordOrgsMap, parentRecordId, entityUId) => {
        return recordOrgsMap?.[`${parentRecordId || ''}|${entityUId}`];
    }
);

export const getCustomRecordsSearchCriteria$ = createSelector(getCustomRecordsEntityMap$, recordsMap => recordsMap?.searchCriteria);

export const getCustomRecordsPagination$ = createSelector(getCustomRecordsEntityMap$, recordsMap => {
    return {
        pageSize: recordsMap?.pageSize || 20,
        pageIndex: recordsMap?.currentPageIndex || 0,
    };
});

export const getCustomEntityNonPaginatedRecordIds$ = createSelector(getCustomRecordsEntityMap$, recordOrgsMap => {
    return recordOrgsMap?.searchCriteria ? recordOrgsMap?.searchRecordIds : recordOrgsMap?.recordIds;
});

export const getCustomEntityRecordIds$ = createSelector(
    getCustomRecordsPagination$,
    getCustomEntityNonPaginatedRecordIds$,
    (pagination, recordIds) => {
        const start = pagination.pageIndex * pagination.pageSize;
        const end = (pagination.pageIndex + 1) * pagination.pageSize;
        return recordIds?.filter((_id, index) => index >= start && index < end);
    }
);

export const getCustomEntityRecords$ = createSelector(
    getCustomEntityRecordIds$,
    getRecords$,
    (recordIds, records) =>
        recordIds?.reduce((entityRecords, recordId) => {
            const record = records[recordId];
            record && entityRecords.push(record);
            return entityRecords;
        }, []) || []
);

export const getCustomSelectedInstanceWorkFlow$ = createSelector(
    getWorkflowInstances$,
    getCustomInstanceId$,
    (workflowMap, selectedInstanceId) => workflowMap?.[selectedInstanceId]
);

export const getCustomEntityRecordChangeLogs$ = createSelector(getRecordLogs$, recordChangeLogs => recordChangeLogs);
